const LICHESS_API_BASE = String(process.env.REACT_APP_LICHESS_API_BASE || 'https://lichess.org').replace(/\/+$/, '');

function parseJsonSafe(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeThemes(value) {
  if (Array.isArray(value)) return value.map((x) => String(x || '').trim().toLowerCase()).filter(Boolean);
  if (typeof value === 'string') return value.split(/[,\s]+/).map((x) => String(x || '').trim().toLowerCase()).filter(Boolean);
  return [];
}

function normalizeMoves(value) {
  if (Array.isArray(value)) return value.map((m) => String(m || '').trim().toLowerCase()).filter(Boolean);
  if (typeof value === 'string') return value.split(/\s+/).map((m) => String(m || '').trim().toLowerCase()).filter(Boolean);
  return [];
}

function normalizePuzzleReplayEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const puzzle = entry.puzzle && typeof entry.puzzle === 'object' ? entry.puzzle : entry;
  const game = entry.game && typeof entry.game === 'object' ? entry.game : {};

  const sourcePuzzleId = String(puzzle.id || puzzle.puzzleId || '').trim();
  const fenBefore = String(
    puzzle.fen || puzzle.fenBefore || game.fen || game.initialFen || ''
  ).trim();
  const moves = normalizeMoves(
    puzzle.solution || puzzle.moves || puzzle.movesUci || entry.solution || entry.moves
  );

  if (!sourcePuzzleId || !fenBefore || moves.length === 0) return null;

  const rating = Number(puzzle.rating || puzzle.glicko || 0) || 0;
  const popularity = Number(puzzle.popularity || 0) || 0;
  const nbPlays = Number(puzzle.plays || puzzle.nbPlays || puzzle.playCount || 0) || 0;
  const themes = normalizeThemes(puzzle.themes || puzzle.tags || []);
  const gameId = String(game.id || '').trim();
  const gameUrl = gameId ? `${LICHESS_API_BASE}/${gameId}` : '';

  return {
    id: `lichess-${sourcePuzzleId}`,
    source: 'lichess-api',
    source_puzzle_id: sourcePuzzleId,
    fen_before: fenBefore,
    moves_uci: moves,
    first_uci: moves[0] || '',
    best_uci: moves[1] || moves[0] || '',
    rating,
    popularity,
    nb_plays: nbPlays,
    themes,
    game_url: gameUrl
  };
}

async function requestLichess(path, { token = '', signal } = {}) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${LICHESS_API_BASE}${path}`, {
    method: 'GET',
    headers,
    signal,
    cache: 'no-store'
  });
  const text = await response.text();
  if (!response.ok) {
    const err = new Error(text || `Lichess request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return text || '';
}

async function requestInternalReplay({ days, theme, limit, signal } = {}) {
  const qs = new URLSearchParams({
    days: String(days),
    theme: String(theme),
    limit: String(limit)
  });
  const response = await fetch(`/api/chessy/lichess/puzzles/replay?${qs.toString()}`, {
    method: 'GET',
    signal,
    cache: 'no-store'
  });
  const text = await response.text();
  if (!response.ok) {
    const err = new Error(text || `Internal replay API failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return parseJsonSafe(text || '{}');
}

export async function fetchLichessPuzzleReplay({
  days = 7,
  theme = 'mix',
  limit = 10,
  token = '',
  signal
} = {}) {
  const safeDays = Math.max(1, Math.min(365, Number(days || 7)));
  const safeLimit = Math.max(1, Number(limit || 10));
  const safeTheme = String(theme || 'mix').trim() || 'mix';

  // Preferred path: project API gateway (stable auth/CORS + future caching/rate-limit).
  try {
    const payload = await requestInternalReplay({
      days: safeDays,
      theme: safeTheme,
      limit: safeLimit,
      signal
    });
    const list = Array.isArray(payload?.rows) ? payload.rows : [];
    if (list.length > 0) return list.slice(0, safeLimit);
  } catch {
    // fallback to direct public API (works in dev if API route is not active)
  }

  const encodedTheme = encodeURIComponent(safeTheme);
  const bodyText = await requestLichess(`/api/puzzle/replay/${safeDays}/${encodedTheme}`, { token, signal });

  let rows = [];
  const asJson = parseJsonSafe(bodyText);
  if (Array.isArray(asJson)) {
    rows = asJson.map(normalizePuzzleReplayEntry).filter(Boolean);
  } else if (asJson && typeof asJson === 'object') {
    rows = [normalizePuzzleReplayEntry(asJson)].filter(Boolean);
  } else {
    rows = bodyText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => parseJsonSafe(line))
      .map(normalizePuzzleReplayEntry)
      .filter(Boolean);
  }

  return rows.slice(0, safeLimit);
}

export async function fetchLichessStudyPgn({
  studyId,
  token = '',
  comments = true,
  variations = true,
  clocks = false,
  signal
} = {}) {
  const safeStudyId = String(studyId || '').trim();
  if (!safeStudyId) throw new Error('Missing studyId');
  const qs = new URLSearchParams({
    comments: String(Boolean(comments)),
    variations: String(Boolean(variations)),
    clocks: String(Boolean(clocks))
  });
  return requestLichess(`/api/study/${encodeURIComponent(safeStudyId)}.pgn?${qs.toString()}`, {
    token,
    signal
  });
}
